import { PortfolioEnterpriseProductServiceProductData } from '@mews-commander/data-access';
import { Container, Stack, Tag, Typography } from '@optimus-web/core';
import { LocalizedCurrency, LocalizedEnum, LocalizedText, lt } from '@mews/framework';
import { Card, DividerList, LabeledListItem } from '@mews-commander/ui';
import { getProductChargingTextKeyForBookableServices } from '@mews/business-components';
import { ProductChargingMode, UnicodeEntity } from '@mews/enums';
import { DeleteServiceProductButton } from './DeleteServiceProductButton';

interface Props {
    servicesData: PortfolioEnterpriseProductServiceProductData[];
    enterpriseProductId: string;
}

export const LocalProductServices = ({ servicesData, enterpriseProductId }: Props) => {
    const services = Object.values(servicesData);

    return services.length > 0 ? (
        <Stack spacing="200" vertical>
            {services.map(
                ({
                    adjustedPrice,
                    adjustment,
                    bookablePeriod,
                    charging,
                    consumptionMoment,
                    options,
                    productId,
                    promotions,
                    service,
                }) => (
                    <Card key={service.id}>
                        <Container padding={['0', '0', '200']}>
                            <Stack itemAlignment="center" distribution="space-between">
                                <Typography textStyle="titleSmallStrong" as="div">
                                    {service.name || service.id}
                                </Typography>
                                <DeleteServiceProductButton
                                    enterpriseProductId={enterpriseProductId}
                                    productId={productId}
                                />
                            </Stack>
                        </Container>
                        <DividerList>
                            <LabeledListItem
                                label={lt('ConfigureProductServicesPaymentsCharging')}
                                data-test-id="productCharging"
                            >
                                <Tag variant="basicSubtle" outline>
                                    <LocalizedText
                                        textKey={getProductChargingTextKeyForBookableServices(
                                            charging as unknown as ProductChargingMode,
                                            bookablePeriod as unknown as Mews.TextKey
                                        )}
                                        substitutes={{ timeUnit: bookablePeriod || '' }}
                                    />
                                </Tag>
                            </LabeledListItem>
                            <LabeledListItem
                                label={lt('ConfigureProductServicesPaymentsTiming')}
                                data-test-id="productConsumption"
                            >
                                {consumptionMoment ? (
                                    <Tag variant="basicSubtle" outline>
                                        <LocalizedEnum prefix="ProductConsumptionMoment" value={consumptionMoment} />
                                    </Tag>
                                ) : (
                                    UnicodeEntity.Dash
                                )}
                            </LabeledListItem>
                            <LabeledListItem
                                label={lt('ProductOptions')}
                                labelOptions={{ whiteSpace: 'nowrap' }}
                                itemAlignment="start"
                                data-test-id="productOptions"
                            >
                                {options?.length ? (
                                    <Stack spacing="50" wrap>
                                        {options.map((option) => (
                                            <Tag key={option} variant="basicSubtle" outline>
                                                <LocalizedEnum prefix="ProductOptions" value={option} />
                                            </Tag>
                                        ))}
                                    </Stack>
                                ) : (
                                    UnicodeEntity.Dash
                                )}
                            </LabeledListItem>
                            <LabeledListItem
                                label={lt('ConfigureProductServicesPromotions')}
                                labelOptions={{ whiteSpace: 'nowrap' }}
                                itemAlignment="start"
                                data-test-id="promotions"
                            >
                                {promotions?.length ? (
                                    <Stack spacing="50" wrap>
                                        {promotions.map((promotion) => (
                                            <Tag key={promotion} variant="basicSubtle" outline>
                                                <LocalizedEnum prefix="Promotions" value={promotion} />
                                            </Tag>
                                        ))}
                                    </Stack>
                                ) : (
                                    UnicodeEntity.Dash
                                )}
                            </LabeledListItem>
                            <LabeledListItem label={lt('GeneralAdjustedPriceTaxExcl')} data-test-id="adjustedPrice">
                                <Stack>
                                    <LocalizedCurrency
                                        value={adjustedPrice.netValue}
                                        currency={adjustedPrice.currency}
                                    />
                                    {adjustment && (
                                        <Typography textStyle="bodyLarge" variant="secondary" as="div">
                                            {UnicodeEntity.NoBreakSpace}
                                            {UnicodeEntity.LeftParenthesis}
                                            <LocalizedEnum
                                                prefix="EnterpriseProductPriceAdjustment"
                                                value={adjustment.discriminator}
                                            />
                                            {UnicodeEntity.RightParenthesis}
                                        </Typography>
                                    )}
                                </Stack>
                            </LabeledListItem>
                        </DividerList>
                    </Card>
                )
            )}
        </Stack>
    ) : (
        <Typography textStyle="bodyMedium" variant="tertiary" as="div">
            {lt('ProductNoServicesConfigured')}
        </Typography>
    );
};
